import { motion } from 'motion/react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { cn } from '@workspace/shadcn';

interface GridPatternProps extends React.SVGProps<SVGSVGElement> {
  width?: number;
  height?: number;
  strokeDasharray?: string | number;
  numSquares?: number;
  maxOpacity?: number;
  duration?: number;
}

export function GridPattern({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  className,
  maxOpacity = 0.5,
  duration = 4,
  ...props
}: GridPatternProps) {
  const id = useId();
  const containerRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [squares, setSquares] = useState<{ id: number; pos: number[] }[]>([]);
  const [layout, setLayout] = useState('');

  const getPos = useCallback(
    () => [
      Math.floor((Math.random() * dimensions.width) / width),
      Math.floor((Math.random() * dimensions.height) / height),
    ],
    [dimensions.width, dimensions.height, width, height],
  );

  // The square field is derived from the measured container, so it is rebuilt
  // during render when that changes rather than in an effect.
  const currentLayout = `${dimensions.width}x${dimensions.height}x${numSquares}`;
  if (dimensions.width && dimensions.height && layout !== currentLayout) {
    setLayout(currentLayout);
    setSquares(
      Array.from({ length: numSquares }, (_, index) => ({
        id: index,
        pos: getPos(),
      })),
    );
  }

  const updateSquarePosition = (squareId: number) => {
    setSquares((current) =>
      current.map((square) =>
        square.id === squareId ? { ...square, pos: getPos() } : square,
      ),
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.unobserve(container);
  }, []);

  return (
    <svg
      ref={containerRef}
      aria-hidden="true"
      className={cn(
        'fill-secondary stroke-secondary/30 dark:stroke-secondary/40 pointer-events-none absolute inset-0 h-full w-full opacity-80 dark:opacity-100',
        className,
      )}
      {...props}
    >
      <defs>
        <pattern
          id={id}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path
            d={`M.5 ${height}V.5H${width}`}
            fill="none"
            strokeDasharray={strokeDasharray}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
      <svg x={x} y={y} className="overflow-visible">
        {squares.map(({ pos: [squareX, squareY], id: squareId }, index) => (
          <motion.rect
            key={`${squareX}-${squareY}-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: maxOpacity }}
            transition={{
              duration,
              repeat: 1,
              delay: index * 0.1,
              repeatType: 'reverse',
            }}
            onAnimationComplete={() => updateSquarePosition(squareId)}
            width={width - 1}
            height={height - 1}
            x={squareX * width + 1}
            y={squareY * height + 1}
            strokeWidth="0"
          />
        ))}
      </svg>
    </svg>
  );
}
